import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { siteGlobalsService } from '@/api/services';

const SiteGlobalsContext = createContext(undefined);

const defaultFooter = { company_name: '', copyright_text: '' };
const COOKIE_CONSENT_STORAGE_KEY = 'southcrete_cookie_consent_v1';
const defaultCookieConsent = {
  initialized: false,
  decision: 'unset',
  analytics: false,
  updatedAt: null,
};

export function useSiteGlobals() {
  const context = useContext(SiteGlobalsContext);
  if (context === undefined) {
    throw new Error('useSiteGlobals must be used within SiteGlobalsProvider');
  }
  return context;
}

function injectStyles(styles) {
  if (!Array.isArray(styles)) return;
  styles.forEach((item) => {
    const url = item?.url?.trim();
    if (!url) return;
    if (document.querySelector(`link[href="${url}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    document.head.appendChild(link);
  });
}

function injectScripts(scripts) {
  if (!Array.isArray(scripts)) return;
  scripts.forEach((item) => {
    const url = item?.url?.trim();
    if (!url) return;
    if (document.querySelector(`script[src="${url}"]`)) return;
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    if (item.position === 'head') {
      document.head.appendChild(script);
    } else {
      document.body.appendChild(script);
    }
  });
}

function injectFonts(fonts) {
  if (!Array.isArray(fonts)) return;
  fonts.forEach((f) => {
    const url = f?.url?.trim();
    if (!url) return;
    if (document.querySelector(`link[href="${url}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    document.head.appendChild(link);
  });
}

function injectGoogleAnalytics(measurementId) {
  const id = (measurementId || '').trim();
  if (!id) return;
  if (document.querySelector('script[data-ga-id]')) return;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(script);
  const config = document.createElement('script');
  config.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${id}');`;
  config.setAttribute('data-ga-id', id);
  document.head.appendChild(config);
}

function injectMicrosoftClarity(projectId) {
  const id = (projectId || '').trim();
  if (!id) return;
  if (document.querySelector('script[data-clarity-id]')) return;
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.setAttribute('data-clarity-id', id);
  script.textContent = `
(function(c,l,a,r,i,t,y){
  c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
  t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
  y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
})(window, document, "clarity", "script", "${id}");
`;
  document.head.appendChild(script);
}

function normalizeCookieConsent(raw) {
  const analytics = Boolean(raw?.analytics);
  const decision =
    raw?.decision === 'all' || raw?.decision === 'necessary-only'
      ? raw.decision
      : analytics
      ? 'all'
      : 'necessary-only';
  const updatedAt = typeof raw?.updatedAt === 'string' && raw.updatedAt.trim() ? raw.updatedAt : null;
  return {
    initialized: true,
    decision,
    analytics,
    updatedAt,
  };
}

function readCookieConsent() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    return normalizeCookieConsent(JSON.parse(raw));
  } catch {
    return null;
  }
}

function persistCookieConsent(nextConsent) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(nextConsent));
}

export function SiteGlobalsProvider({ children }) {
  const [footer, setFooter] = useState(defaultFooter);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [integrations, setIntegrations] = useState({});
  const [cookieConsent, setCookieConsent] = useState(defaultCookieConsent);
  const injected = useRef(false);

  useEffect(() => {
    const storedConsent = readCookieConsent();
    if (storedConsent) {
      setCookieConsent(storedConsent);
      return;
    }
    setCookieConsent((prev) => ({ ...prev, initialized: true }));
  }, []);

  useEffect(() => {
    if (injected.current) return;
    injected.current = true;

    siteGlobalsService
      .get()
      .then((res) => {
        if (!res?.data?.success || res.data.data == null) return;
        const data = res.data.data;

        if (Array.isArray(data.styles)) injectStyles(data.styles);
        if (Array.isArray(data.scripts)) injectScripts(data.scripts);
        if (Array.isArray(data.fonts)) injectFonts(data.fonts);

        const nextIntegrations =
          data.integrations && typeof data.integrations === 'object' ? data.integrations : {};
        setIntegrations(nextIntegrations);

        if (data.footer && typeof data.footer === 'object' && !Array.isArray(data.footer)) {
          setFooter({
            company_name: data.footer.company_name ?? '',
            copyright_text: data.footer.copyright_text ?? '',
          });
        }
      })
      .catch((err) => {
        setError(err?.message || 'Failed to load site globals');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!cookieConsent.initialized || !cookieConsent.analytics) return;
    const gaId = integrations?.google_analytics_id;
    const clarityId = integrations?.microsoft_clarity_id;
    if (gaId && String(gaId).trim()) injectGoogleAnalytics(gaId);
    if (clarityId && String(clarityId).trim()) injectMicrosoftClarity(clarityId);
  }, [cookieConsent, integrations]);

  const setCookieConsentDecision = ({ analytics }) => {
    const normalized = normalizeCookieConsent({
      decision: analytics ? 'all' : 'necessary-only',
      analytics: Boolean(analytics),
      updatedAt: new Date().toISOString(),
    });
    persistCookieConsent(normalized);
    setCookieConsent(normalized);
  };

  const value = {
    footer,
    loading,
    error,
    cookieConsent,
    setCookieConsentDecision,
  };

  return (
    <SiteGlobalsContext.Provider value={value}>
      {children}
    </SiteGlobalsContext.Provider>
  );
}
